import * as THREE from 'three';
import { compileObjects, type Drawable } from '../scene-warmup.js';
import { FLOOR_FULL, FLOOR_START, FRONT_FULL, FRONT_START, SceneryOcclusion, towardCamera } from './scenery-occlusion.js';
import { pickTerrainGround } from './terrain-picking.js';

const MAX_HEROES = 10;
const TEXELS = MAX_HEROES * 3 + 1;
// Openings fade in quickly, hold briefly and fade out, so a probe flipping at a ridge or tree edge cannot make the scenery flicker.
const OPEN_MS = 120, HOLD_MS = 200, CLOSE_MS = 300;
// Like the fog uniforms, these bind the scene being drawn. Each scene owns and releases its own small texture.
const uniforms = { characterCutouts: { value: null as THREE.DataTexture | null }, cutoutUp: { value: new THREE.Vector3() },
  cutoutToward: { value: new THREE.Vector3() }, cutoutEdge: { value: 0 } };
const adopted = new WeakSet<THREE.Material>();
const terrainMaterials = new WeakSet<THREE.Material>();
export const OCCLUDED_OPACITY = .3;

const PARS = `
varying vec3 vCutoutView;
uniform highp sampler2D characterCutouts;
uniform vec3 cutoutUp;
uniform vec3 cutoutToward;
uniform float cutoutEdge;
uniform float cutoutTerrain;
float cutoutOpacity = 1.;
vec4 cutoutAt(int i) { return texelFetch(characterCutouts, ivec2(i, 0), 0); }
void cutoutScenery() {
  if (cutoutEdge < -.5) return;
  int count = int(cutoutAt(${MAX_HEROES * 3}).x);
  float opening = 0.;
  for (int i = 0; i < ${MAX_HEROES}; i++) {
    if (i >= count) break;
    vec4 foot = cutoutAt(i * 3), head = cutoutAt(i * 3 + 1), open = cutoutAt(i * 3 + 2);
    float strength = cutoutTerrain > .5 ? open.x : open.y;
    if (strength <= 0.) continue;
    // Only scenery between the hero and the camera on the ground plan opens: a slope or tree behind them stays solid however
    // high it rises on screen. The soft ramp keeps swaying foliage from shimmering across the boundary.
    vec3 offset = vCutoutView - foot.xyz;
    float front = smoothstep(${FRONT_START.toFixed(2)}, ${FRONT_FULL.toFixed(2)}, dot(offset, cutoutToward));
    // Do not remove the floor.
    if (cutoutTerrain > .5) front *= smoothstep(${FLOOR_START.toFixed(2)}, ${FLOOR_FULL.toFixed(2)}, dot(offset, cutoutUp));
    if (front <= 0.) continue;
    float distanceToHero = length(vCutoutView.xy - (foot.xy + head.xy) * .5) / foot.w;
    opening = max(opening, strength * front * (1. - smoothstep(.9, 1., distanceToHero)));
  }
  cutoutOpacity = 1. - opening * ${(1 - OCCLUDED_OPACITY).toFixed(2)};
  if (cutoutEdge > .5) {
    if (cutoutOpacity <= 0. || cutoutOpacity >= 1.) discard;
  } else if (cutoutOpacity < 1.) discard;
}
`;

export interface CutoutHero { position: THREE.Vector3; height: number; visible: boolean }
interface Fade { terrain: number; prop: number; terrainSeen: number; propSeen: number; at: number }

function approach(value: number, open: boolean, elapsed: number) {
  return open ? Math.min(1, value + elapsed / OPEN_MS) : Math.max(0, value - elapsed / CLOSE_MS);
}

/** Circular openings in scenery, with a narrow transparent rim drawn over the revealed world. Shadows stay intact. */
export class CharacterOcclusion {
  private data = new Float32Array(TEXELS * 4);
  private texture = new THREE.DataTexture(this.data, TEXELS, 1, THREE.RGBAFormat, THREE.FloatType);
  private foot = new THREE.Vector3();
  private head = new THREE.Vector3();
  private count = 0;
  private surfaces = new WeakSet<THREE.Mesh>();
  private edges = new Map<THREE.Material, { version: number; material: THREE.Material }>();
  private edgeScene = Object.assign(new THREE.Scene(), { matrixWorldAutoUpdate: false });
  private edgeMeshes: THREE.Mesh[] = [];
  private originals: (THREE.Material | THREE.Material[])[] = [];
  private omitted = new THREE.MeshBasicMaterial({ visible: false });
  private fills = new Map<THREE.Material, { version: number; material: THREE.Material }>();
  private visibility = new SceneryOcclusion();
  private terrain: THREE.Mesh | null = null;
  private viewMatrix = new THREE.Matrix4();
  private up = new THREE.Vector3();
  private toward = new THREE.Vector3();
  private point = new THREE.Vector3();
  private sphere = new THREE.Sphere();
  private fades = new WeakMap<THREE.Vector3, Fade>();
  private active = false;
  private emptyScene = new THREE.Scene();
  private emptyCamera = new THREE.Camera();
  private emptyMaterial = new THREE.ShaderMaterial({
    vertexShader: 'void main() { gl_Position = vec4(position.xy, 1., 1.); }',
    fragmentShader: 'void main() { gl_FragColor = vec4(0.); }',
    colorWrite: false, depthWrite: false, depthFunc: THREE.EqualDepth, toneMapped: false,
    stencilWrite: true, stencilRef: 1, stencilFunc: THREE.AlwaysStencilFunc, stencilZPass: THREE.ReplaceStencilOp,
  });
  private emptyGeometry = new THREE.BufferGeometry().setAttribute('position', new THREE.Float32BufferAttribute([-1, -1, 0, 3, -1, 0, -1, 3, 0], 3));
  constructor() {
    this.texture.generateMipmaps = false;
    const mask = new THREE.Mesh(this.emptyGeometry, this.emptyMaterial); mask.frustumCulled = false; this.emptyScene.add(mask);
  }

  setTerrain(mesh: THREE.Mesh) {
    this.terrain = mesh; this.visibility.setTerrain(mesh);
    for (const material of Array.isArray(mesh.material) ? mesh.material : [mesh.material]) terrainMaterials.add(material);
  }

  /** Adopt map roots, including terrain, before warming. Figures, effects, water and shadow-only materials stay intact. */
  adopt(root: THREE.Object3D) {
    root.traverse(object => {
      if (!(object instanceof THREE.Mesh)) return;
      for (const material of Array.isArray(object.material) ? object.material : [object.material]) {
        if (adopted.has(material) || material.transparent || !material.depthTest || !material.colorWrite
          || !((material as THREE.MeshStandardMaterial).isMeshStandardMaterial || (material as THREE.MeshBasicMaterial).isMeshBasicMaterial
            || (material as THREE.MeshLambertMaterial).isMeshLambertMaterial || (material as THREE.MeshPhongMaterial).isMeshPhongMaterial)) continue;
        adopted.add(material);
        const compile = material.onBeforeCompile, previousKey = material.customProgramCacheKey;
        const key = previousKey === THREE.Material.prototype.customProgramCacheKey ? compile.toString() : previousKey.call(material);
        material.onBeforeCompile = function (this: THREE.Material, shader: THREE.WebGLProgramParametersWithUniforms, renderer: THREE.WebGLRenderer) {
          compile.call(this, shader, renderer);
          Object.assign(shader.uniforms, uniforms);
          shader.uniforms.cutoutTerrain = { value: terrainMaterials.has(material) ? 1 : 0 };
          // Read the final view position after instancing, batching and tree wind have projected the vertex.
          const end = shader.vertexShader.lastIndexOf('}');
          shader.vertexShader = 'varying vec3 vCutoutView;\n' + shader.vertexShader.slice(0, end) + '\nvCutoutView = mvPosition.xyz;\n}';
          shader.fragmentShader = PARS + shader.fragmentShader
            .replace('#include <clipping_planes_fragment>', '#include <clipping_planes_fragment>\ncutoutScenery();')
            .replace('#include <opaque_fragment>', 'diffuseColor.a *= cutoutOpacity;\n#include <opaque_fragment>');
        };
        material.customProgramCacheKey = () => key + '|character-cutout';
        material.needsUpdate = true;
      }
      if ((Array.isArray(object.material) ? object.material : [object.material]).some((material: THREE.Material) => adopted.has(material))) {
        this.surfaces.add(object);
        if (!(Array.isArray(object.material) ? object.material : [object.material]).some((material: THREE.Material) => terrainMaterials.has(material))) this.visibility.add(object);
      }
    });
  }

  /** Uses interpolated positions and the final (possibly shaken) camera, so openings stay attached during movement and zoom. */
  update(camera: THREE.Camera, heroes: Iterable<CutoutHero>, now = performance.now()) {
    camera.updateMatrixWorld();
    const view = camera.matrixWorldInverse;
    this.viewMatrix.copy(view); this.up.set(0, 1, 0).transformDirection(view); this.active = false;
    towardCamera(camera, this.toward).transformDirection(view);
    let count = 0;
    for (const hero of heroes) {
      if (!hero.visible || count === MAX_HEROES) continue;
      this.foot.copy(hero.position).applyMatrix4(view);
      this.head.copy(hero.position); this.head.y += hero.height; this.head.applyMatrix4(view);
      // Diameter is five times the projected full standing height; view-space XY keeps the opening circular on screen.
      const radius = Math.hypot(this.head.x - this.foot.x, this.head.y - this.foot.y) * 2.5;
      const flags = this.visibility.flags(camera, hero.position, hero.height, now);
      let fade = this.fades.get(hero.position);
      // A hero who appears already hidden opens at once; afterwards each kind of opening eases in and out.
      if (!fade) this.fades.set(hero.position, fade = { terrain: flags & 1, prop: flags >> 1 & 1, terrainSeen: now, propSeen: now, at: now });
      const elapsed = Math.min(1000, Math.max(0, now - fade.at)); fade.at = now;
      if (flags & 1) fade.terrainSeen = now;
      if (flags & 2) fade.propSeen = now;
      fade.terrain = approach(fade.terrain, !!(flags & 1) || now - fade.terrainSeen < HOLD_MS, elapsed);
      fade.prop = approach(fade.prop, !!(flags & 2) || now - fade.propSeen < HOLD_MS, elapsed);
      this.active ||= fade.terrain > 0 || fade.prop > 0;
      this.data.set([this.foot.x, this.foot.y, this.foot.z, radius, this.head.x, this.head.y, this.head.z, 0, fade.terrain, fade.prop, 0, 0], count++ * 12);
    }
    this.data[MAX_HEROES * 12] = count;
    this.count = count;
    this.texture.needsUpdate = true;
    uniforms.characterCutouts.value = this.texture;
    uniforms.cutoutUp.value.copy(this.up);
    uniforms.cutoutToward.value.copy(this.toward);
    uniforms.cutoutEdge.value = 0;
  }

  /** Ground picking follows the same terrain mask as the picture; solid fallback surfaces remain selectable. */
  groundPoint(raycaster: THREE.Raycaster, accept?: (point: THREE.Vector3) => boolean) {
    return this.terrain ? pickTerrainGround(this.terrain, raycaster, point => !this.terrainHidden(point) && (!accept || accept(point))) : null;
  }

  /** Mirrors the terrain branch of the shader: ground counts as hidden once it is mostly faded. */
  terrainHidden(point: THREE.Vector3) {
    const p = this.point.copy(point).applyMatrix4(this.viewMatrix), data = this.data, up = this.up, toward = this.toward;
    for (let index = 0; index < this.count; index++) {
      const i = index * 12, strength = data[i + 8];
      if (strength <= 0) continue;
      const x = p.x - data[i], y = p.y - data[i + 1], z = p.z - data[i + 2];
      const front = THREE.MathUtils.smoothstep(x * toward.x + y * toward.y + z * toward.z, FRONT_START, FRONT_FULL)
        * THREE.MathUtils.smoothstep(x * up.x + y * up.y + z * up.z, FLOOR_START, FLOOR_FULL);
      const distance = Math.hypot(p.x - (data[i] + data[i + 4]) * .5, p.y - (data[i + 1] + data[i + 5]) * .5) / data[i + 3];
      if (strength * front * (1 - THREE.MathUtils.smoothstep(distance, .9, 1)) > .5) return true;
    }
    return false;
  }

  /** Whether a mesh's bounds reach an opening of its kind on screen; the rim passes skip everything else. */
  private reaches(mesh: THREE.Mesh) {
    const source = mesh instanceof THREE.InstancedMesh ? mesh.boundingSphere ?? (mesh.computeBoundingSphere(), mesh.boundingSphere!)
      : mesh.geometry.boundingSphere ?? (mesh.geometry.computeBoundingSphere(), mesh.geometry.boundingSphere!);
    const sphere = this.sphere.copy(source).applyMatrix4(mesh.matrixWorld).applyMatrix4(this.viewMatrix), data = this.data;
    const strength = (Array.isArray(mesh.material) ? mesh.material : [mesh.material]).some(material => terrainMaterials.has(material)) ? 8 : 9;
    for (let index = 0; index < this.count; index++) {
      const i = index * 12;
      if (data[i + strength] > 0 && Math.hypot(sphere.center.x - (data[i] + data[i + 4]) * .5, sphere.center.y - (data[i + 1] + data[i + 5]) * .5) < data[i + 3] + sphere.radius) return true;
    }
    return false;
  }

  private edgeMaterial(source: THREE.Material, fill = false): THREE.Material {
    if (!adopted.has(source)) return this.omitted;
    const cache = fill ? this.fills : this.edges;
    let entry = cache.get(source);
    if (!entry || entry.version !== source.version) {
      entry?.material.dispose();
      const material = source.clone();
      material.onBeforeCompile = (shader, renderer) => source.onBeforeCompile.call(source, shader, renderer);
      // The fill is the scenery's own shader with stencil and depth state, which three.js keeps out of the program: sharing the original's key
      // reuses its program instead of linking a copy of every scenery shader behind the loading overlay. The rim is transparent, its own program.
      material.customProgramCacheKey = fill ? () => source.customProgramCacheKey() : () => source.customProgramCacheKey() + '|cutout-edge';
      material.userData.noFogOfWar = true;
      material.transparent = !fill; material.depthWrite = fill; material.forceSinglePass = true;
      if (fill) { material.stencilWrite = true; material.stencilRef = 1; material.stencilFunc = THREE.EqualStencilFunc; }
      cache.set(source, entry = { version: source.version, material });
    }
    // Conditions can change a lamp's colour or brightness without rebuilding its shader.
    const original = source as THREE.MeshStandardMaterial, edge = entry.material as THREE.MeshStandardMaterial;
    edge.color.copy(original.color); edge.opacity = original.opacity;
    if (original.emissive) { edge.emissive.copy(original.emissive); edge.emissiveIntensity = original.emissiveIntensity; }
    if (original.roughness !== undefined) { edge.roughness = original.roughness; edge.metalness = original.metalness; }
    return entry.material;
  }

  /** Link the rim's transparent variants behind the loading overlay, alongside the ordinary scenery shaders. */
  async prepare(renderer: THREE.WebGLRenderer, scene: THREE.Scene, camera: THREE.Camera, objects: readonly Drawable[], active: () => boolean) {
    this.visibility.prepare();
    const surfaces = objects.filter((object): object is THREE.Mesh => object instanceof THREE.Mesh && this.surfaces.has(object));
    // Every batch is started before any is awaited, so the driver links them all at once (awaited one by one, the coast took 8 s more).
    const linking: Promise<unknown>[] = [];
    for (const fill of [false, true]) for (let offset = 0; offset < surfaces.length && active(); offset += 48) {
      const batch = surfaces.slice(offset, offset + 48), originals = batch.map(mesh => mesh.material);
      try {
        batch.forEach(mesh => { mesh.material = Array.isArray(mesh.material) ? mesh.material.map(material => this.edgeMaterial(material, fill)) : this.edgeMaterial(mesh.material, fill); });
        const compiling = compileObjects(renderer, scene, camera, batch);
        compiling.catch(() => {}); // reported once, through `Promise.all` below
        linking.push(compiling);
      } finally { batch.forEach((mesh, index) => { mesh.material = originals[index]; }); }
      await new Promise(resolve => setTimeout(resolve, 0));
    }
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      await Promise.race([Promise.all(linking), new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(new Error('Cutout rim shader preparation timed out')), 30_000);
      })]);
    } finally { clearTimeout(timer); }
  }

  /** Borrow the visible map meshes and lights, with their world matrices already updated by the world draw. */
  renderEdges(renderer: THREE.WebGLRenderer, scene: THREE.Scene, camera: THREE.Camera) {
    if (!this.active) return;
    const meshes = this.edgeMeshes, originals = this.originals, children = this.edgeScene.children;
    meshes.length = originals.length = children.length = 0;
    scene.traverseVisible(object => {
      if (!object.layers.test(camera.layers)) return;
      if (object instanceof THREE.Light) children.push(object);
      else if (object instanceof THREE.Mesh && this.surfaces.has(object) && this.reaches(object)) {
        meshes.push(object); originals.push(object.material); children.push(object);
      }
    });
    if (!meshes.length) { children.length = 0; return; }
    const autoClear = renderer.autoClear, shadow = renderer.shadowMap, autoUpdate = shadow.autoUpdate, needsUpdate = shadow.needsUpdate;
    this.edgeScene.fog = scene.fog; this.edgeScene.environment = scene.environment;
    this.edgeScene.environmentIntensity = scene.environmentIntensity; this.edgeScene.environmentRotation.copy(scene.environmentRotation);
    try {
      renderer.autoClear = false; shadow.autoUpdate = false; shadow.needsUpdate = false;
      // Mark only clear-depth pixels. Original scenery fills these, so an opening can never expose the underside or sky.
      renderer.clear(false, false, true);
      renderer.render(this.emptyScene, this.emptyCamera);
      meshes.forEach((mesh, index) => {
        const original = originals[index];
        mesh.material = Array.isArray(original) ? original.map(material => this.edgeMaterial(material, true)) : this.edgeMaterial(original, true);
      });
      uniforms.cutoutEdge.value = -1;
      renderer.render(this.edgeScene, camera);
      renderer.clear(false, false, true);
      meshes.forEach((mesh, index) => {
        const original = originals[index];
        mesh.material = Array.isArray(original) ? original.map(material => this.edgeMaterial(material)) : this.edgeMaterial(original);
      });
      uniforms.cutoutEdge.value = 1;
      renderer.render(this.edgeScene, camera);
    } finally {
      meshes.forEach((mesh, index) => { mesh.material = originals[index]; });
      uniforms.cutoutEdge.value = 0;
      renderer.autoClear = autoClear; shadow.autoUpdate = autoUpdate; shadow.needsUpdate = needsUpdate;
      meshes.length = originals.length = children.length = 0;
    }
  }

  dispose() {
    if (uniforms.characterCutouts.value === this.texture) uniforms.characterCutouts.value = null;
    this.texture.dispose();
    for (const { material } of this.edges.values()) material.dispose();
    for (const { material } of this.fills.values()) material.dispose();
    this.edges.clear(); this.fills.clear(); this.omitted.dispose(); this.emptyGeometry.dispose(); this.emptyMaterial.dispose();
  }
}
